// =============================================================================
// VINUM - API Client
// Migrated from Flutter ApiClient with EXACT behavior
// =============================================================================

import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../constants';
import type { ApiError, AuthTokens } from '../types';
import { getStorage } from '../utils/storage';

// Re-export for backwards compatibility
export type { StorageAdapter } from '../utils/storage';
export { configureStorage as setStorageAdapter } from '../utils/storage';

// -----------------------------------------------------------------------------
// Token Management
// Exact match with Flutter token handling
// -----------------------------------------------------------------------------

let accessToken: string | null = null;
let refreshToken: string | null = null;
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

async function loadTokens(): Promise<void> {
  const storage = getStorage();
  const accessResult = storage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshResult = storage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const rawAccess = accessResult instanceof Promise ? await accessResult : accessResult;
  const rawRefresh = refreshResult instanceof Promise ? await refreshResult : refreshResult;
  accessToken = rawAccess && rawAccess !== 'null' ? rawAccess : null;
  refreshToken = rawRefresh && rawRefresh !== 'null' ? rawRefresh : null;
}

async function saveTokens(tokens: AuthTokens): Promise<void> {
  const storage = getStorage();
  accessToken = tokens.accessToken;
  refreshToken = tokens.refreshToken;
  const setAccess = storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
  const setRefresh = storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
  if (setAccess instanceof Promise) await setAccess;
  if (setRefresh instanceof Promise) await setRefresh;
}

async function clearTokens(): Promise<void> {
  const storage = getStorage();
  accessToken = null;
  refreshToken = null;
  const removeAccess = storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  const removeRefresh = storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  const removeUser = storage.removeItem(STORAGE_KEYS.USER_DATA);
  if (removeAccess instanceof Promise) await removeAccess;
  if (removeRefresh instanceof Promise) await removeRefresh;
  if (removeUser instanceof Promise) await removeUser;
}

function onRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

// -----------------------------------------------------------------------------
// Session Expired Callback
// -----------------------------------------------------------------------------

let onSessionExpired: (() => void) | null = null;

export function setOnSessionExpired(callback: () => void): void {
  onSessionExpired = callback;
}

// -----------------------------------------------------------------------------
// API Error Class
// Exact match with Flutter ApiException
// -----------------------------------------------------------------------------

export class ApiException extends Error {
  statusCode: number;
  code?: string;

  constructor(message: string, statusCode: number, code?: string) {
    super(message);
    this.name = 'ApiException';
    this.statusCode = statusCode;
    this.code = code;
  }

  static fromAxiosError(error: AxiosError<ApiError>): ApiException {
    const statusCode = error.response?.status || 500;
    const data = error.response?.data;

    let message = 'Si è verificato un errore';
    let code: string | undefined;

    if (data) {
      message = data.message || message;
      code = data.code;
    } else if (error.message) {
      message = error.message;
    }

    return new ApiException(message, statusCode, code);
  }
}

// -----------------------------------------------------------------------------
// Axios Instance Creation
// -----------------------------------------------------------------------------

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  // Request interceptor - Add auth token
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Load tokens if not loaded
      if (!accessToken) {
        await loadTokens();
      }

      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - Handle auth failures and token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const status = error.response?.status;
      const responseMessage = String(error.response?.data?.message || '').toLowerCase();
      const isTokenFailure403 =
        status === 403 &&
        (responseMessage.includes('token non valido') ||
          responseMessage.includes('token scaduto') ||
          responseMessage.includes('token mancante'));

      // Handle auth expiration (401 standard + legacy 403 token failures)
      if ((status === 401 || isTokenFailure403) && !originalRequest._retry) {
        if (isRefreshing) {
          // Wait for refresh to complete
          return new Promise((resolve) => {
            addRefreshSubscriber((token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(client(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          if (!refreshToken) {
            throw new Error('No refresh token');
          }

          // Attempt token refresh
          const response = await axios.post<{ accessToken: string; refreshToken: string }>(
            `${API_CONFIG.BASE_URL}/auth/refresh`,
            { refreshToken }
          );

          const newTokens = response.data;
          await saveTokens(newTokens);

          isRefreshing = false;
          onRefreshed(newTokens.accessToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          }
          return client(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          await clearTokens();

          // Trigger session expired callback
          if (onSessionExpired) {
            onSessionExpired();
          }

          return Promise.reject(ApiException.fromAxiosError(error));
        }
      }

      return Promise.reject(ApiException.fromAxiosError(error));
    }
  );

  return client;
}

// -----------------------------------------------------------------------------
// Singleton API Client
// -----------------------------------------------------------------------------

export const apiClient = createApiClient();

// -----------------------------------------------------------------------------
// API Helper Methods
// -----------------------------------------------------------------------------

export async function initializeApiClient(): Promise<void> {
  await loadTokens();
}

export async function setAuthTokens(tokens: AuthTokens): Promise<void> {
  await saveTokens(tokens);
}

export async function clearAuthTokens(): Promise<void> {
  await clearTokens();
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  return refreshToken;
}

export function isAuthenticated(): boolean {
  return !!accessToken;
}

// -----------------------------------------------------------------------------
// Generic API Methods
// -----------------------------------------------------------------------------

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

export async function apiPost<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

export async function apiPut<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
}

export async function apiPatch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}
